import { RegimeStatus } from './constants.js';

export function countIndependentDimensions(evidence) {
  const eligible = evidence.filter((item) => item.supportsCandidate && !item.contradictsCandidate && item.provenanceComplete && !item.recallDerived);
  const lineagesByDimension = new Map();
  for (const item of eligible) {
    if (!lineagesByDimension.has(item.dimension)) lineagesByDimension.set(item.dimension, new Set());
    lineagesByDimension.get(item.dimension).add(item.lineageId);
  }
  const matchedDimensionByLineage = new Map();
  const dimensions = [...lineagesByDimension.keys()].sort();
  const findMatch = (dimension, visitedLineages) => {
    for (const lineage of [...lineagesByDimension.get(dimension)].sort()) {
      if (visitedLineages.has(lineage)) continue;
      visitedLineages.add(lineage);
      const priorDimension = matchedDimensionByLineage.get(lineage);
      if (!priorDimension || findMatch(priorDimension, visitedLineages)) {
        matchedDimensionByLineage.set(lineage, dimension);
        return true;
      }
    }
    return false;
  };
  return dimensions.reduce((count, dimension) => count + Number(findMatch(dimension, new Set())), 0);
}

export const REQUIRED_TRUE_PREDICATES = Object.freeze([
  'independentEvidence', 'provenanceComplete', 'contradictionFree', 'recallPathValid',
  'reflexPathValid', 'stableRegime', 'stabilizationComplete'
]);

export function hasCompleteSatisfiedPredicateVector(vector) {
  return Boolean(vector && REQUIRED_TRUE_PREDICATES.every((key) => vector[key] === true));
}

export function evaluatePredicates({ evidence, regime, stabilizationRequirement = 5, directRecallAttempt = false, directReflexAttempt = false }) {
  const independentDimensionCount = countIndependentDimensions(evidence);
  const provenanceComplete = evidence.length > 0 && evidence.every((item) => item.provenanceComplete);
  const contradictionFree = evidence.every((item) => !item.contradictsCandidate);
  const stableRegime = regime.status === RegimeStatus.STABLE;
  const stabilizationComplete = regime.status === RegimeStatus.STABLE && regime.stableSteps >= stabilizationRequirement;
  return Object.freeze({
    independentEvidence: independentDimensionCount >= 2,
    independentDimensionCount,
    requiredDimensionCount: 2,
    provenanceComplete,
    contradictionFree,
    recallPathValid: !directRecallAttempt,
    reflexPathValid: !directReflexAttempt,
    stableRegime,
    stabilizationComplete
  });
}

export function failedReasonCodes(p) {
  const reasons = [];
  if (!p.independentEvidence) reasons.push('INSUFFICIENT_INDEPENDENT_DIMENSIONS');
  if (!p.provenanceComplete) reasons.push('PROVENANCE_INCOMPLETE');
  if (!p.contradictionFree) reasons.push('CONTRADICTORY_EVIDENCE');
  if (!p.recallPathValid) reasons.push('RECALL_CANNOT_DIRECTLY_PROMOTE');
  if (!p.reflexPathValid) reasons.push('REFLEX_CANNOT_DIRECTLY_WRITE');
  if (!p.stableRegime) reasons.push('REGIME_NOT_STABLE');
  if (!p.stabilizationComplete) reasons.push('STABILIZATION_INCOMPLETE');
  return reasons;
}
